import "./index.scss";

import { Box, Stack } from "@mui/material";
import Button from "components/Button";
import ConfirmationBox from "components/ConfirmationBox";
import ScreenLoader from "components/ScreenLoader";
import { TSearchableItem } from "components/SearchableList";
import Tabs from "components/Tabs";
import { MYSQL_DATE_FORMAT } from "constants/index";
import { scrollToTop } from "helpers/utils";
import _ from "lodash";
import { TMember } from "models/member";
import {
  getCurrentFacility,
  getLkpCompanyList,
  getLkpHealthConditions,
  getLkpHealthConditionsCategories,
  getLkpInstructorList,
  getLkpInterestCategories,
  getLkpInterests,
  getLkpRandomFitlinxxId,
  verifyFitlinxxIdAvailability,
} from "modules/lookup/lookupServices";
import { getMember } from "modules/member/memberServices";
import moment from "moment";
import {
  createContext,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useAppSelector } from "redux/hooks";

import ContactsTab from "./components/ContactsTab";
import ExerciseReadinessTab from "./components/ExerciseReadinessTab";
import HealthConditionsTab from "./components/HealthConditionsTab";
import InterestsTab from "./components/InterestsTab";
import PrimaryDetailsTab from "./components/PrimaryDetailsTab";
interface OptionSet {
  [key: string]: {
    id: string;
    label: string;
    options: TSearchableItem[];
  };
}

export const MemberFormContext = createContext({ isEditMode: false });

const MemberForm = forwardRef(
  (
    props: {
      open: boolean;
      onClose: any;
      memberId?: number;
      sendMemberBackCallback: Function; //This is the function to be called with the member object when it is ready/valid. The parent component will take care of actually saving the object in the database when its workflow sees fit (memberFormModal:handleMemberObjectUpdated())
      takeMeBack?: Function; //This is the function to take the user back when clicking on back on the first screen
      switchToTab?: string;
    },
    ref
  ) => {
    const { open, onClose, memberId, sendMemberBackCallback, takeMeBack, switchToTab } = props;
    const currentUserId = useAppSelector((state) => state.auth.authUser.user.lngMemberIDcnt); //This will be used to select default selected instructor
    const { settings } = useAppSelector((state) => state.facilityOptions);

    const [tabs, setTabs] = useState([]);
    const [tabValue, setTabValue] = useState("primary_details");
    const [loading, setLoading] = useState(false);
    const [lookupsLoadded, setLookupsLoadded] = useState(false);
    const [isEditMode, setIsEditMode] = useState(memberId !== undefined);
    const [existingMemberFitlinxxId, setExistingMemberFitlinxxId] = useState("");
    const [instructorOptions, setInstructorOptions] = useState<TSearchableItem[]>([]);
    const [companyOptions, setCompanyOptions] = useState<any[]>([]);
    const [healthOptionsSets, setHealthOptionsSets] = useState<OptionSet>({});
    const [
      isExerciseReadinessAllYesWarningDisplayed,
      setIsExerciseReadinessAllYesWarningDisplayed,
    ] = useState(false);
    const exerciseReadinessRef = useRef(null);
    const [interestsOptionsSets, setInterestsOptionsSets] = useState<OptionSet>({});
    const [showPhysicianConsentWarningModal, setShowPhysicianConsentWarningModal] = useState(false);
    const today = new Date();

    const filteredTabs = useMemo(() => {
      let result = _.cloneDeep(tabs);
      const { exercise_readiness_tab_on, health_conditions_tab_on, specify_interests_tab_on } =
        settings?.OPTIONS;
      if (exercise_readiness_tab_on && !exercise_readiness_tab_on?.value) {
        result = result.filter((t) => t.value !== "exercise_readiness");
      }
      if (health_conditions_tab_on && !health_conditions_tab_on?.value) {
        result = result.filter((t) => t.value !== "health_conditions");
      }
      if (specify_interests_tab_on && !specify_interests_tab_on?.value) {
        result = result.filter((t) => t.value !== "interests");
      }
      return result;
    }, [tabs, settings]);

    const defaultEmptyValue: TMember = {
      address: "",
      address_2: "",
      birth_date: moment(new Date()).subtract(10, "year").format(MYSQL_DATE_FORMAT),
      cancellation_date: "",
      cancellation_reason: null,
      change_settings: false,
      change_weights: false,
      city: "",
      company_name: "",
      country: "",
      email: "",
      exercise_readiness: [],
      expiration_date: "",
      first_name: "",
      fitlinxx_id: "",
      health_conditions: [],
      home_facility: "",
      interests: [],
      last_name: "",
      member_id: 0,
      nick_name: "",
      other_contact_name: "",
      other_contact_phone: "",
      other_contact_relationship: "",
      other_member_id: "",
      other_phone: "",
      phone: "",
      physician_name: "",
      physician_phone: "",
      physician_speciality: "",
      postal_code: "",
      primary_contact_name: "",
      primary_contact_phone: "",
      primary_contact_relationship: "",
      secondary_contact_name: "",
      secondary_contact_phone: "",
      secondary_contact_relationship: "",
      sex: "",
      sign_up_date: today.toString(),
      state: "",
      status: "FITLINXX_FREE",
      trainer_id: currentUserId,
      training_partner_sounds: 1,
      weight: 0,
    };
    const methods = useForm<TMember>({
      defaultValues: defaultEmptyValue,
    });
    const { trigger, setValue, getValues, reset, formState } = methods;

    useEffect(() => {
      if (open) {
        setLoading(true);

        let promises: Promise<any>[] = [];

        //Get the member if member ID is provided
        if (memberId) {
          getMember({ member_id: memberId }).then((res) => {
            if (res.success) {
              setExistingMemberFitlinxxId(res.data.fitlinxx_id);
              reset(res.data);
            }
          });
        }

        if (!lookupsLoadded) {
          if (!memberId) {
            //Get random value for FitLinxx ID
            promises.push(
              getLkpRandomFitlinxxId().then((res) => {
                if (res.success) {
                  setValue("fitlinxx_id", res.data);
                }
              })
            );
          }

          //Get current facility
          //TODO: double check, the below could be saved part of facility options
          promises.push(
            getCurrentFacility().then((res) => {
              if (res.success) {
                setValue("home_facility", `${res.data.id} - ${res.data.name}`);
                if (!memberId) {
                  setValue("country", res.data.country);
                  setValue("state", res.data.state);
                }
              }
            })
          );

          //Get instructors
          promises.push(
            getLkpInstructorList().then((res) => {
              if (res.success) {
                let entries: any[] = [];
                res.data.forEach((e) => {
                  entries.push({ label: `${e.first_name} ${e.last_name}`, value: e.instructor_id });
                });
                setInstructorOptions(entries);
              }
            })
          );

          //Get companies
          promises.push(
            getLkpCompanyList().then((res) => {
              if (res.success) {
                let entries: any[] = [];
                res.data.forEach((e) => {
                  entries.push({ id: e.employer, label: `${e.employer}` });
                });
                setCompanyOptions(entries);
              }
            })
          );

          //Get Health lists
          promises.push(
            new Promise((resolve) => {
              getLkpHealthConditionsCategories().then((res) => {
                if (res.success) {
                  let subPromises: Promise<any>[] = [];
                  let healthConditionsCategories: any[] = [];
                  let optionSetsToReturn: OptionSet = {};
                  Object.keys(res.data).forEach((e) => {
                    healthConditionsCategories.push({
                      label: res.data[e],
                      value: e,
                    });
                  });
                  healthConditionsCategories.forEach((e) => {
                    optionSetsToReturn[e.value] = {
                      id: e.value,
                      label: e.label,
                      options: [],
                    };
                    subPromises.push(
                      getLkpHealthConditions({
                        health_condition_category: e.value,
                      }).then((r) => {
                        if (r.success) {
                          optionSetsToReturn[e.value] = {
                            id: e.value,
                            label: e.label,
                            options: r.data.map((d) => {
                              const tsearchableItem: TSearchableItem = {
                                label: d.title,
                                value: d.id,
                              };
                              return tsearchableItem;
                            }),
                          };
                        }
                      })
                    );
                  });

                  Promise.all(subPromises).then((_v) => {
                    setHealthOptionsSets(optionSetsToReturn);
                    resolve(true);
                  });
                }
              });
            })
          );

          //Get Interests' lists
          promises.push(
            new Promise((resolve) => {
              getLkpInterestCategories().then((res) => {
                if (res.success) {
                  let subPromises: Promise<any>[] = [];
                  let interestCategories: any[] = [];
                  let optionSetsToReturn: OptionSet = {};
                  Object.keys(res.data).forEach((e) => {
                    interestCategories.push({ label: res.data[e], value: e });
                  });
                  interestCategories.forEach((e) => {
                    optionSetsToReturn[e.value] = {
                      id: e.value,
                      label: e.label,
                      options: [],
                    };
                    subPromises.push(
                      getLkpInterests({
                        interest_category: e.value,
                        limit: 0,
                      }).then((r) => {
                        if (r.success) {
                          optionSetsToReturn[e.value] = {
                            id: e.value,
                            label: e.label,
                            options: r?.data
                              ?.filter((d) => d.status === "ACTIVE")
                              ?.map((d) => {
                                const tsearchableItem: TSearchableItem = {
                                  label: d.title,
                                  value: d.id,
                                };
                                return tsearchableItem;
                              }),
                          };
                        }
                      })
                    );
                  });
                  Promise.all(subPromises).then((_v) => {
                    setInterestsOptionsSets(optionSetsToReturn);
                    resolve(true);
                  });
                }
              });
            })
          );
        }

        Promise.all(promises).then((_v) => {
          setTabs([
            { label: "Primary Details", value: "primary_details" },
            { label: "Exercise Readiness", value: "exercise_readiness" },
            { label: "Health Conditions", value: "health_conditions" },
            { label: "Contacts", value: "contacts" },
            { label: "Interests", value: "interests" },
          ]);
          if (switchToTab) {
            setTabValue(switchToTab); //When opening, open the first tab all the time
          } else {
            setTabValue("primary_details"); //When opening, open the first tab all the time
          }
          setLoading(false);
          setLookupsLoadded(true);
        });
      }
    }, [open]);

    const submitButtonLabel = useMemo(() => {
      const currentTabIndex = filteredTabs.findIndex((f) => f.value === tabValue);
      if (currentTabIndex === filteredTabs.length - 1 && !takeMeBack) {
        return "Save";
      } else {
        return "Next";
      }
    }, [filteredTabs, tabValue]);

    useImperativeHandle(ref, () => ({
      getUpdates() {
        return getValues();
      },
    }));

    //#region event handlers
    const handleCloseModal = useCallback(() => {
      onClose();
    }, []);

    const handleChangeTabByClickOnTab = (tabId) => {
      const currentTabIndex = filteredTabs.findIndex((f) => f.value === tabValue);
      navigateFromTabIndex(currentTabIndex, tabId);
    };

    const handleNextClick = () => {
      //Get the current tab
      const currentTabIndex = filteredTabs.findIndex((f) => f.value === tabValue);
      navigateFromTabIndex(currentTabIndex);
    };

    const navigateFromTabIndex = (currentTabIndex: number, toTab?: string) => {
      scrollToTop(".personal-details-modal-container");
      //console.log(`Signup Date is now: `, getValues("sign_up_date"));
      //If I'm on first tab, need to validate
      if (currentTabIndex === 0) {
        trigger().then((validationResult) => {
          if (!validationResult) {
            return;
          }
          //Verifying fitlinxx ID
          const fitlinxxId = getValues("fitlinxx_id");
          if (existingMemberFitlinxxId === fitlinxxId) {
            setTabValue(toTab || filteredTabs[1].value);
            sendMemberBackCallback(getValues());
            return;
          }
          if (fitlinxxId && existingMemberFitlinxxId !== fitlinxxId) {
            verifyFitlinxxIdAvailability({ fitlinxx_id: fitlinxxId })
              .then((res) => {
                setTabValue(toTab || filteredTabs[1].value);
                sendMemberBackCallback(getValues());
              })
              .catch((e) => {
                toast(e[0], { type: "error" });
                setValue("fitlinxx_id", "");
                return;
              });
          }
          return;
        });
        return;
      }
      //If I'm on second tab, need to validate that all questions have a yes/no answer
      //TODO: update the logic below when Facility options exercise readiness is optional
      if (currentTabIndex === 1 && exerciseReadinessRef.current !== null) {
        const exerciseReadinessValidation = exerciseReadinessRef.current.validate();
        if (exerciseReadinessValidation) {
          sendMemberBackCallback(getValues());
          if (exerciseReadinessValidation >= 2 && !isExerciseReadinessAllYesWarningDisplayed) {
            setShowPhysicianConsentWarningModal(true);
          } else {
            setTabValue(toTab || filteredTabs[2].value);
          }
        } else {
          toast("Select Yes or No for each fitness assessment question", { type: "error" });
        }
        return;
      }
      //If we are at contacts tab, validate it
      if (tabValue === "contacts") {
        trigger().then((validationResult) => {
          if (!validationResult) {
            return;
          }
          if (currentTabIndex === filteredTabs.length - 1) {
            sendMemberBackCallback(getValues(), true);
          } else {
            setTabValue(toTab || "interests");
            sendMemberBackCallback(getValues());
          }
        });
        return;
      }
      //Otherwise keep moving through tabs and at the last tab
      if (currentTabIndex === filteredTabs.length - 1) {
        if (toTab) {
          setTabValue(toTab);
        } else {
          sendMemberBackCallback(getValues(), true);
        }
      } else {
        //Move to the next tab
        setTabValue(toTab || filteredTabs[currentTabIndex + 1].value);
        sendMemberBackCallback(getValues());
        //If we reached the last tab, callback
      }
    };

    const handleClosePhysicianConsentWarningModal = (v) => {
      setIsExerciseReadinessAllYesWarningDisplayed(true);
      setShowPhysicianConsentWarningModal(false);
    };

    const handleBackClick = () => {
      const currentTabIndex = filteredTabs.findIndex((f) => f.value === tabValue);

      if (currentTabIndex !== 0) {
        setTabValue(filteredTabs[currentTabIndex - 1].value);
      } else {
        takeMeBack();
      }
    };
    //#endregion event handlers

    if (!open) return null;

    return (
      <>
        <MemberFormContext.Provider value={{ isEditMode }}>
          <Box className="memberForm">
            <Tabs
              style={{ marginBottom: 30, marginTop: 30 }}
              value={tabValue}
              onChange={handleChangeTabByClickOnTab}
              tabs={filteredTabs}
            />
            <FormProvider {...methods}>
              <form>
                {loading && <ScreenLoader isScreenLoader />}
                {!loading && (
                  <>
                    <Stack height={"100%"}>
                      {tabValue === "primary_details" && (
                        <PrimaryDetailsTab
                          instructorOptions={instructorOptions}
                          companyOptions={companyOptions}
                        />
                      )}
                      {tabValue === "exercise_readiness" && (
                        <ExerciseReadinessTab
                          exerciseReadinessOptionsSets={
                            (healthOptionsSets &&
                              healthOptionsSets["EXERCISE_READINESS"].options) ||
                            []
                          }
                          ref={exerciseReadinessRef}
                        />
                      )}
                      {tabValue === "health_conditions" && (
                        <HealthConditionsTab healthOptionsSets={healthOptionsSets} />
                      )}
                      {tabValue === "contacts" && <ContactsTab />}
                      {tabValue === "interests" && (
                        <InterestsTab interestsOptionsSets={interestsOptionsSets} />
                      )}
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-end"
                      marginTop="30px"
                    >
                      <Stack direction="row" alignItems="center" spacing={3} flexGrow={1}>
                        <Box flexGrow={1}>
                          {(takeMeBack || (!takeMeBack && tabValue !== "primary_details")) && (
                            <Button
                              label="Back"
                              type="button"
                              className="text-button"
                              onClick={() => handleBackClick()}
                            />
                          )}
                        </Box>

                        <Button
                          type="button"
                          label="Cancel"
                          className="text-button"
                          onClick={handleCloseModal}
                        />
                        <Button
                          label={submitButtonLabel}
                          className="confirm-button"
                          type="button"
                          onClick={handleNextClick}
                        />
                      </Stack>
                    </Stack>
                  </>
                )}
              </form>
            </FormProvider>
          </Box>
        </MemberFormContext.Provider>
        <ConfirmationBox
          open={showPhysicianConsentWarningModal}
          onClose={handleClosePhysicianConsentWarningModal}
          title={"Physician Consent Needed"}
          message={
            <>
              The participant questionnaire indicates that the member must have a physician's
              consent and sign a release form prior to using FitLinxx.
            </>
          }
          type="alert"
        />
      </>
    );
  }
);

export default MemberForm;
